import { usePostApprovalLetter } from '@features/instructors/hooks/use-post-approval-letter/usePostApprovalLetter';
import { useQueryClient } from '@tanstack/react-query';
import * as React from 'react';

import type {
  UploadableLetterTarget,
  UseApprovalLetterUploadParams,
  UseApprovalLetterUploadResult,
} from './useApprovalLetterUpload.types';

import { getPostApprovalLetterError } from './useApprovalLetterUpload.utils';

export function useApprovalLetterUpload(
  params: UseApprovalLetterUploadParams
): UseApprovalLetterUploadResult {
  const { customerId } = params;
  const queryClient = useQueryClient();

  const [selectedTarget, setSelectedTarget] =
    React.useState<UploadableLetterTarget | null>(null);
  const [uploadedCaseIds, setUploadedCaseIds] = React.useState<Set<string>>(
    new Set()
  );

  const selectedTargetRef = React.useRef<UploadableLetterTarget | null>(null);
  selectedTargetRef.current = selectedTarget;

  const {
    mutate: uploadLetter,
    isPending,
    error: uploadMutationError,
    reset: resetUploadMutation,
  } = usePostApprovalLetter({ customerId });

  const uploadError = getPostApprovalLetterError(uploadMutationError);

  const handleUploadClick = React.useCallback(
    (target: UploadableLetterTarget): void => {
      resetUploadMutation();
      setSelectedTarget(target);
    },
    [resetUploadMutation]
  );

  const handleModalClose = React.useCallback((): void => {
    setSelectedTarget(null);
    resetUploadMutation();
  }, [resetUploadMutation]);

  const handleFileChange = React.useCallback((): void => {
    resetUploadMutation();
  }, [resetUploadMutation]);

  const handleUpload = React.useCallback(
    (file: File, signal?: AbortSignal): void => {
      if (!selectedTargetRef.current) return;

      const { instructorId, caseId } = selectedTargetRef.current;

      uploadLetter(
        { instructorId, caseId, file, signal },
        {
          onSuccess: () => {
            setSelectedTarget(null);
            setUploadedCaseIds(prev => new Set([...prev, caseId]));
            queryClient.invalidateQueries({
              queryKey: ['instructors', 'pending-letter', customerId],
            });
            queryClient.invalidateQueries({
              queryKey: ['instructors', customerId],
            });
          },
        }
      );
    },
    [uploadLetter, queryClient, customerId]
  );

  return {
    selectedTarget,
    uploadedCaseIds,
    isPending,
    uploadError,
    handleUploadClick,
    handleModalClose,
    handleFileChange,
    handleUpload,
  };
}
